import type { AvailabilityResponse, MenuResponse } from '@cartazo/contracts'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, apiBaseUrl } from '@/lib/api'
import { type NormalizedMenu, normalizeMenu } from './normalize'

/**
 * Carga y mantiene fresca la carta de un restaurante.
 *
 * Implementa la estrategia central del sistema: el árbol se pide una vez y se
 * cachea agresivamente; lo que cambia durante el servicio llega por un endpoint
 * de disponibilidad de 1–3 KB.
 *
 * El aviso de que ese overlay cambió llega por SSE, no por sondeo: el trigger de
 * Postgres emite `menu_changed` y el servidor lo reenvía. El sondeo sigue ahí
 * como red de seguridad —hay redes de restaurante que matan las conexiones
 * largas— pero cuando el stream está vivo se espacia a cinco minutos.
 */

/** Sondeo sin stream. Suficiente para que "Agotado hoy" se note, sin castigar la batería. */
const AVAILABILITY_POLL_MS = 45_000

/**
 * Sondeo con stream vivo.
 *
 * No se desactiva del todo aposta: si el stream se queda colgado sin dar error
 * —un proxy que traga los bytes en silencio— este sondeo es lo único que impide
 * que la carta muestre un plato agotado toda la noche.
 */
const AVAILABILITY_POLL_LIVE_MS = 300_000

export type MenuStatus = 'loading' | 'ready' | 'error'

export type UseMenuResult = {
  status: MenuStatus
  menu: NormalizedMenu | null
  error: string | null
  /** `true` si lo que se muestra viene del Service Worker y la red falló. */
  stale: boolean
  /** `true` si los cambios llegan por stream en vez de por sondeo. */
  live: boolean
  reload(): void
}

export function useMenu(slug: string | null): UseMenuResult {
  const [raw, setRaw] = useState<MenuResponse | null>(null)
  const [status, setStatus] = useState<MenuStatus>('loading')
  const [error, setError] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null)
  const [live, setLive] = useState(false)

  const menuEtag = useRef<string | null>(null)
  const availabilityEtag = useRef<string | null>(null)
  /**
   * ¿Hay ya una carta en pantalla?
   *
   * Se guarda en una ref y no se lee del estado para que `load` no dependa de
   * `raw`. Si dependiera, cada carga produciría un `load` nuevo, que dispararía
   * el efecto, que cargaría otra vez: un bucle infinito de peticiones.
   */
  const hasMenu = useRef(false)

  const load = useCallback(async () => {
    if (!slug) return

    try {
      const result = await api.getMenu(slug, { etag: menuEtag.current })
      menuEtag.current = result.etag

      // 304: lo que ya tenemos sigue vigente y no hay nada que repintar.
      if (!result.notModified && result.data) {
        setRaw(result.data)
        writeBootCache(result.data)
      }

      hasMenu.current = true
      setStatus('ready')
      setStale(false)
      setError(null)
    } catch (cause) {
      // Si ya hay carta en pantalla, un fallo de red no debe borrarla: el
      // comensal sigue pudiendo leerla mientras vuelve la señal.
      if (hasMenu.current) {
        setStale(true)
        return
      }
      setStatus('error')
      setError(cause instanceof Error ? cause.message : 'No pudimos cargar la carta')
    }
  }, [slug])

  // Carga inicial y al cambiar de restaurante.
  useEffect(() => {
    hasMenu.current = false
    void load()
  }, [load])

  /**
   * Pide el overlay de disponibilidad.
   *
   * Vive fuera del efecto de sondeo porque tienen ciclos de vida distintos: el
   * stream también lo dispara, y duplicarla dentro de cada efecto habría hecho
   * que un cambio de intervalo reiniciara la conexión SSE.
   */
  const pollAvailability = useCallback(async () => {
    if (!slug || document.visibilityState !== 'visible') return

    try {
      const result = await api.getAvailability(slug, { etag: availabilityEtag.current })
      availabilityEtag.current = result.etag
      if (!result.notModified && result.data) setAvailability(result.data)
    } catch {
      // Best-effort: un fallo aquí no debe romper nada visible.
    }
  }, [slug])

  /**
   * Aviso en tiempo real de que el catálogo cambió.
   *
   * El evento NO trae la disponibilidad, solo dice que algo cambió: la carta
   * responde pidiendo el overlay que ya sabe pedir. Mandar el contenido por el
   * stream obligaría a mantener dos formatos del mismo dato.
   */
  useEffect(() => {
    if (!slug || status !== 'ready' || typeof EventSource === 'undefined') return

    const source = new EventSource(`${apiBaseUrl}/menu/${slug}/stream`)

    source.addEventListener('open', () => setLive(true))
    source.addEventListener('menu_changed', () => void pollAvailability())

    // `EventSource` reconecta solo, así que un error es casi siempre transitorio.
    // Lo único que hace falta es volver al sondeo rápido mientras dure.
    source.addEventListener('error', () => setLive(false))

    return () => {
      setLive(false)
      source.close()
    }
  }, [slug, status, pollAvailability])

  /*
   * Primera lectura y regreso a la pestaña. NO depende de `live`.
   *
   * Estaba junto al temporizador, en un efecto que sí dependía de `live`, y eso
   * hacía que CADA vez que el stream conectaba o se caía se disparase una
   * petición extra. En un móvil con mala cobertura el stream reconecta sin
   * parar, y aquello se convertía en una tormenta de peticiones justo en la red
   * en la que todo el diseño intenta ser cuidadoso.
   */
  useEffect(() => {
    if (!slug || status !== 'ready') return

    // Volver a la pestaña es el momento de mayor riesgo de mostrar datos viejos:
    // el comensal pudo tenerla en segundo plano media hora, y con la pestaña
    // oculta el navegador pudo suspender también el stream.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void pollAvailability()
    }

    document.addEventListener('visibilitychange', onVisible)
    void pollAvailability()

    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [slug, status, pollAvailability])

  // Sondeo de disponibilidad: camino principal sin stream, red de seguridad con
  // él. Aquí sí importa `live`, y lo único que cambia es el intervalo.
  useEffect(() => {
    if (!slug || status !== 'ready') return

    const timer = setInterval(
      () => void pollAvailability(),
      live ? AVAILABILITY_POLL_LIVE_MS : AVAILABILITY_POLL_MS,
    )

    return () => clearInterval(timer)
  }, [slug, status, live, pollAvailability])

  /**
   * Superpone la disponibilidad sobre el árbol cacheado.
   *
   * Se aplica en memoria y no se pide la carta de nuevo: es exactamente lo que
   * justifica tener dos endpoints separados.
   */
  const menu = useMemo(() => {
    if (!raw) return null
    if (!availability) return normalizeMenu(raw)

    const unavailableProducts = new Set(availability.unavailable_product_ids)
    const unavailableOptions = new Set(availability.unavailable_option_ids)

    if (unavailableProducts.size === 0 && unavailableOptions.size === 0) {
      return normalizeMenu(raw)
    }

    const patched: MenuResponse = {
      ...raw,
      categories: raw.categories.map((category) => ({
        ...category,
        subcategories: category.subcategories.map((subcategory) => ({
          ...subcategory,
          products: subcategory.products.map((product) => ({
            ...product,
            is_available: product.is_available && !unavailableProducts.has(product.id),
            modifiers: product.modifiers.map((group) => ({
              ...group,
              options: group.options.map((option) => ({
                ...option,
                is_available: option.is_available && !unavailableOptions.has(option.id),
              })),
            })),
          })),
        })),
      })),
    }

    return normalizeMenu(patched)
  }, [raw, availability])

  return { status, menu, error, stale, live, reload: () => void load() }
}

/**
 * Guarda lo mínimo para que la PRÓXIMA visita pinte la marca antes del primer
 * frame. Lo lee el script inline de `index.html`.
 */
function writeBootCache(menu: MenuResponse): void {
  try {
    localStorage.setItem(
      'cartazo:boot',
      JSON.stringify({
        theme: menu.restaurant.theme_default,
        brand: {
          primary: menu.restaurant.brand.primary,
          accent: menu.restaurant.brand.accent,
          'on-accent': menu.restaurant.brand.on_accent,
        },
      }),
    )
  } catch {
    // Sin almacenamiento no hay anti-FOUC, pero la carta funciona igual.
  }
}
